import React, { useMemo, useCallback, useState, useContext } from 'react';
import CISideNavbar from './CISideNavbar';
import CIQuestionContentArea from './CIQuestionContentArea';
import Breadcrumb from '../../../../components/Breadcrumb';
import { TriggerGenerateDocumentSection } from '../../../../utils/TriggerGenerateDocumentSection';
import { isPatientFemale } from '../../../../utils/conditionChecks';
import { mapCIDisplayTitle } from '../../../../utils/clinicalInterviewWorkflow';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { useSectionExitRegeneration } from '../../../../hooks/useSectionExitRegeneration';
import { useAuth } from '../../../../hooks/useAuth';
import { auth } from '../../../../firebase';
import {
  buildOptimisticMedicalHistorySection,
  triggerSemblePhysicalHealthRegenIfDirty,
} from '../../../../utils/semblePhysicalHealthStaleness';

// Section keys 1–9 are prefilled sections sharing the parameterized route.
// Navigating between them keeps the ClinicalInterview container mounted.
// Keys 10 (MSE), 11 (RSK), 12 (DF) are separate routes with custom UIs.
const PREFILLED_KEYS = new Set(['1', '2', '3', '4', '5', '6', '7', '8', '9']);

export default function CIPrefilledPage({
  patientId,
  currentStageTitle,
  currentSectionTitle,
  SECTIONS_CI,
  notes,
  setNotes,
  navigate,
  patientDataModel,
  updatePatientFirestore,
  canEdit,
  wf
}) {

  // Pull flushPendingUpdates directly from context so we don't have
  // to thread it as a prop through every parent CI page component.
  const { flushPendingUpdates } = useContext(PatientDataContext);

  // Clinician's organisation drives whether the contract-type badge is shown
  // (only orgs that categorise patients by contract type surface it). Reading
  // it here keeps the presentational content area free of auth coupling.
  const { claims } = useAuth();

  const currentSectionNumber = useMemo(() => {
    const entry = SECTIONS_CI.find(
      section => section.sectionTitle === currentSectionTitle
    );
    return entry ? entry.key : '1';
  }, [SECTIONS_CI, currentSectionTitle]);

  const currentIndex = useMemo(() => SECTIONS_CI.findIndex(
    section => section.sectionTitle === currentSectionTitle
  ), [SECTIONS_CI, currentSectionTitle]);

  const getSectionDetails = useCallback((index) => {
    if (index < 0 || index >= SECTIONS_CI.length) {
      return { link: '', title: '' };
    }
    const section = SECTIONS_CI[index];
    return { link: section.route, title: mapCIDisplayTitle(section.sectionTitle, wf) };
  }, [SECTIONS_CI, wf]);

  const { link: prevSectionLink, title: initialPrevSectionTitle } = getSectionDetails(currentIndex - 1);
  const { title: initialNextSectionTitle } = getSectionDetails(currentIndex + 1);

  let prevSectionTitle = initialPrevSectionTitle;
  let nextSectionTitle = initialNextSectionTitle;

  if (currentIndex === 0) {
    prevSectionTitle = 'Patient Overview';
  }

  const questions = useMemo(() => SECTIONS_CI.find(
    section => section.sectionTitle === currentSectionTitle
  )?.subsections, [SECTIONS_CI, currentSectionTitle]);

  const sendSectionUpdates = useCallback((newUpdates) => {
    return updatePatientFirestore(patientId, {
      '2_clinicalInterview': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore]);

  const [hasChanges, setHasChanges] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Conditions array for the exit-regeneration hook (mirrors handleSectionCompletion)
  const exitRegenConditions = useMemo(() => {
    const conds = [];
    if (isPatientFemale(patientDataModel)) conds.push('patientIsFemale');
    return conds;
  }, [patientDataModel]);

  // Semble MDT physical-health regeneration on section exit.
  //
  // The Semble MDT "Physical Health" paragraph is composed from the CI Medical
  // History form — panels 8.1 (height / weight / BMI / pulse / blood pressure),
  // 8.2 (health conditions / cardiac symptoms / heart surgery) and 8.4 (current
  // medications), plus each panel's clinician notes.  When those inputs change
  // after the Semble report was generated, we regenerate ONLY that paragraph so
  // the EHR copy stays in sync.
  //
  // This deliberately fires on section EXIT, not on note blur: a clinician may
  // edit panel 1 then panel 2 in one sitting, and we only want a single regen
  // when they leave Medical History.  It self-gates (section title, Semble flag,
  // report-generated, and an input dirty-check) so it is a cheap no-op in every
  // other case.  It runs on both exit paths, mirroring exactly how the CI
  // paragraph itself is regenerated:
  //   - managed exits (Next / Prev / Sidebar) → handleSectionCompletion below
  //   - unmanaged exits (breadcrumb / TopNavBar / browser back) → passed to
  //     useSectionExitRegeneration as onUnhandledExit
  const runSemblePhysicalHealthRegenCheck = useCallback(() => {
    if (!canEdit || currentSectionTitle !== 'Medical History' || !wf?.isSembleMdtReportEnabled) {
      return;
    }
    // Overlay the in-memory panel notes onto the (possibly stale) context
    // section so the dirty-check reflects the clinician's latest edits even
    // before the debounced Firestore write and context refresh land.
    const medSectionOverride = buildOptimisticMedicalHistorySection(patientDataModel, notes);
    triggerSemblePhysicalHealthRegenIfDirty({
      patientId,
      clinicianId: auth.currentUser?.uid,
      patientDataModel,
      isSembleMdtReportEnabled: wf.isSembleMdtReportEnabled,
      medSectionOverride,
    });
  }, [canEdit, currentSectionTitle, wf, patientDataModel, notes, patientId]);

  const { markRegenHandled, resetRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: currentStageTitle,
    sectionTitle: currentSectionTitle,
    hasChanges,
    canEdit,
    patientDataModel,
    conditions: exitRegenConditions,
    onUnhandledExit: runSemblePhysicalHealthRegenCheck,
  });

  const handleNotesChange = useCallback((panelIndex, newNotes) => {
    if (!canEdit) return;

    setNotes((prevNotes) => ({
      ...prevNotes,
      [panelIndex]: newNotes,
    }));

    setHasChanges(true);
  }, [setNotes, canEdit]);

  // Persists the current note value to Firestore on blur.
  //
  // IMPORTANT: this handler intentionally does NOT call setHasChanges(true).
  // Dirty-tracking is handled exclusively by handleNotesChange (the onChange
  // handler). The blur handler's sole job is data persistence. Combining the
  // two caused false-positive dirty checks: the DictationTextArea's unmount
  // safety-net can fire onBlur even when no text was edited, which would
  // incorrectly flag the page as dirty and trigger the navigation guard.
  const handleNotesBlur = useCallback((panelIndex) => {
    if (!canEdit) return;

    // Compare against the Firestore-stored value. Subsections are 1-indexed
    // in the data model, so panelIndex 0 maps to subsection key '1'.
    const currentValue = notes[panelIndex] ?? '';
    const storedValue = questions?.[String(panelIndex + 1)]?.clinicianNotes || '';

    const action = currentValue === storedValue ? 'skipped' : 'queued';

    if (action === 'skipped') return;

    updatePatientFirestore(patientId, {
      '2_clinicalInterview': {
        sections: {
          [currentSectionNumber]: {
            subsections: {
              [panelIndex + 1]: {
                clinicianNotes: notes[panelIndex],
              }
            }
          },
        },
      },
    });

  }, [patientId, currentSectionNumber, currentSectionTitle, notes, questions, updatePatientFirestore, canEdit]);

  // Marks the current section complete (if not already), flushes pending
  // writes, and conditionally triggers AI paragraph generation chained to
  // the flush Promise.  Returns the "data ready" Promise so dialog flows
  // can await it while navigation handlers fire-and-forget.
  //
  // Granular guards avoid unnecessary Firestore transactions on revisits:
  //   - needsStatusWrite: false when section is already completed
  //   - needsGeneration: false when no edits AND paragraph already exists
  //   - True no-op: both false — returns immediately with no I/O
  const handleSectionCompletion = useCallback((patientId, currentStageTitle, currentSectionTitle, hasChanges) => {
    if (!canEdit) return Promise.resolve();

    const currentSectionData = patientDataModel?.dataModel?.['2_clinicalInterview']
      ?.sections?.[currentSectionNumber];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';

    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']
      ?.sections?.['1']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(currentSectionTitle) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges || !isCurrentSectionGenerated;

    // True no-op: nothing to write, nothing to generate — avoid any
    // Firestore interaction on revisits with no edits.  Still re-check Semble
    // physical-health staleness when leaving Medical History (self-gated).
    if (!needsStatusWrite && !needsGeneration) {
      runSemblePhysicalHealthRegenCheck();
      return Promise.resolve();
    }

    // Only queue the status update when the section hasn't been marked
    // complete yet. This avoids redundant writes on revisits.
    if (needsStatusWrite) {
      sendSectionUpdates({ status: 'completed', progress: 100 });
    }

    // noTimeout: true ensures the promise only resolves when the queue
    // is genuinely drained — prevents TriggerGenerateDocumentSection
    // from reading stale data on slow networks.
    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      const conditions = [];
      if (isPatientFemale(patientDataModel)) {
        conditions.push("patientIsFemale");
      }
      TriggerGenerateDocumentSection(patientId, currentStageTitle, currentSectionTitle, conditions, dataReady);
    }

    // Semble physical-health regen runs after the write queue drains so the
    // targeted CF reads the just-saved notes.  Fire-and-forget: it never
    // blocks navigation and self-gates to a no-op outside Medical History.
    dataReady.then(runSemblePhysicalHealthRegenCheck);

    return dataReady;
  }, [sendSectionUpdates, patientDataModel, canEdit, flushPendingUpdates, currentSectionNumber, runSemblePhysicalHealthRegenCheck]);

  // ─── Section navigation ──────────────────────────────────────────────
  // For prefilled sections (keys 1–9), the URL updates with replace: true
  // so the ClinicalInterview container stays mounted (instant navigation).
  // For MSE/RSK/DF (keys 10–12), we await flushPendingUpdates then perform
  // a full route navigation since they are separate component trees.
  const navigateToSection = useCallback((index) => {
    markRegenHandled();
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    resetRegenHandled();
    setHasChanges(false);

    const section = SECTIONS_CI[index];
    if (!section?.route) return;

    if (PREFILLED_KEYS.has(section.key)) {
      navigate(`/patient/${patientId}${section.route}`, { replace: true });
      // Smooth scroll to top — the container stays mounted on prefilled-to-
      // prefilled transitions so the browser won't reset scroll automatically.
      const mainContentArea = document.querySelector('.flex-1.overflow-y-auto');
      if (mainContentArea) {
        mainContentArea.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      setIsNavigating(true);
      flushPendingUpdates().then(() => {
        navigate(`/patient/${patientId}${section.route}`);
      });
    }
  }, [patientId, navigate, SECTIONS_CI, hasChanges, handleSectionCompletion, currentStageTitle, currentSectionTitle, markRegenHandled, resetRegenHandled, flushPendingUpdates]);

  const handleNextSection = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < SECTIONS_CI.length) {
      navigateToSection(nextIndex);
    }
  }, [currentIndex, SECTIONS_CI.length, navigateToSection]);

  const handlePrevSection = useCallback(() => {
    if (currentIndex === 0) {
      navigate(`/patient/${patientId}`);
      return;
    }
    navigateToSection(currentIndex - 1);
  }, [currentIndex, navigateToSection, navigate, patientId]);

  const breadcrumbTuples = [

    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [currentSectionTitle]
  ];

  return (
    <>
      <Breadcrumb tupleList={breadcrumbTuples} />      
      <div className="flex">
        <CISideNavbar
          sections={SECTIONS_CI}
          currentSectionIndex={currentIndex}
          navigateToSection={navigateToSection}
          isNavigating={isNavigating}
          wf={wf}
        />
        <CIQuestionContentArea
          patientId={patientId}
          prevSectionLink={prevSectionLink}
          prevSectionTitle={prevSectionTitle}
          nextSectionTitle={nextSectionTitle}
          currentSectionNumber={currentSectionNumber}
          currentSectionTitle={currentSectionTitle}
          questions={questions}
          notes={notes}
          handleNotesChange={handleNotesChange}
          handleNotesBlur={handleNotesBlur}
          handleNextSection={handleNextSection}
          handlePrevSection={handlePrevSection}
          navigate={navigate}
          patientFormData={patientDataModel?.formData}
          organisation={claims?.organisation}
          assessmentType={patientDataModel?.customFields?.assessmentType}
          subContractType={patientDataModel?.customFields?.subContractType}
          canEdit={canEdit}
          isNavigating={isNavigating}
        />
      </div>
    </>
  );
}
